import { createResource, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { VaultAttachmentRecord } from "@boveda/powersync";
import { getItemAttachmentsQuery, queryKeys } from "@boveda/shared";
import { createPowerSyncQuery } from "@boveda/shared/solid";
import { Download, Paperclip, Trash2 } from "@fleet/icons";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { useVault } from "../vault-context";

interface DecryptedAttachmentRow {
  readonly id: string;
  readonly filename: string;
  readonly mime: string;
  readonly size: number;
  readonly bytes: Uint8Array;
}

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;

function formatBytes(n: number): string {
  if (n < BYTES_PER_KB) {
    return `${n} B`;
  }
  if (n < BYTES_PER_MB) {
    return `${(n / BYTES_PER_KB).toFixed(1)} KB`;
  }
  return `${(n / BYTES_PER_MB).toFixed(1)} MB`;
}

/** Downloads decrypted attachment (plain bytes → Blob → anchor). */
function downloadAttachment(att: DecryptedAttachmentRow): void {
  // Copies to own ArrayBuffer for Blob (100% decrypted on client).
  const copy = new Uint8Array(att.bytes.length);
  copy.set(att.bytes);
  const blob = new Blob([copy], { type: att.mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = att.filename;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Encrypted attachments of an item (personal or org vault). Attaching
 * encrypts bytes on client (per-attachment content key); download
 * decrypts on client and delivers original file byte-by-byte. Server
 * only sees ciphertext.
 *
 * `vaultId` scopes `deleteAttachment` to correct vault (`scope` of
 * `patchRowVerified`, §4.14 of `infra/PENDING_WORK.md`): without it, an
 * attachment `id` from ANOTHER org vault of same user could be reached
 * same, because `org_vaults` syncs all user vaults, not just open one.
 */
export function AttachmentsPanel(props: { itemId: string; vaultId: string }) {
  const { t } = useI18n();
  const vault = useVault();
  const { db } = usePowerSyncContext();
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  const attachmentsQuery = createPowerSyncQuery<VaultAttachmentRecord>({
    queryKey: () => queryKeys.vault.attachments(props.itemId),
    queryFn: () => {
      const drizzle = vault.drizzle();
      return drizzle ? getItemAttachmentsQuery(drizzle, props.itemId) : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()),
  });

  // Decrypts each attachment (meta + bytes) for list and download.
  const [decrypted] = createResource(
    () => attachmentsQuery.data ?? [],
    async (rows) => {
      const out: DecryptedAttachmentRow[] = [];
      for (const row of rows) {
        try {
          const { meta, bytes } = await vault.openAttachment(row);
          out.push({
            id: row.id,
            filename: meta.filename,
            mime: meta.mime ?? "application/octet-stream",
            size: row.size ?? bytes.length,
            bytes,
          });
        } catch {
          // Unreadable attachment (wrong key / corrupted): omitted.
        }
      }
      return out;
    },
  );

  async function handleFile(file: File): Promise<void> {
    setError("");
    setBusy(true);
    try {
      const buffer = await file.arrayBuffer();
      await vault.attachFile(
        props.itemId,
        file.name,
        file.type || "application/octet-stream",
        new Uint8Array(buffer),
      );
    } catch {
      setError(t(msg`Could not attach the file.`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="flex flex-col gap-2" data-testid="attachments-panel">
      <div class="flex items-center justify-between">
        <span class="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Paperclip class="h-3.5 w-3.5" /> {t(msg`Attachments`)}
        </span>
        <label
          class="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1 text-xs font-medium transition-colors hover:bg-primary/5"
          data-testid="attachment-add-label"
        >
          <input
            type="file"
            class="sr-only"
            data-testid="attachment-input"
            disabled={busy()}
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              if (file) {
                void handleFile(file);
              }
              e.currentTarget.value = "";
            }}
          />
          {busy() ? t(msg`Encrypting…`) : t(msg`Attach file`)}
        </label>
      </div>

      <Show when={error()}>
        <span class="text-xs text-destructive" role="alert">
          {error()}
        </span>
      </Show>

      <Show
        when={(decrypted() ?? []).length > 0}
        fallback={<span class="text-xs text-muted-foreground">{t(msg`No attachments.`)}</span>}
      >
        <ul class="flex flex-col gap-1" data-testid="attachment-list">
          <For each={decrypted() ?? []}>
            {(att) => (
              <li
                class="flex items-center gap-2 rounded-md border border-border/50 bg-card/40 px-2.5 py-1.5"
                data-testid="attachment-item"
              >
                <Paperclip class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm" data-testid="attachment-name">
                    {att.filename}
                  </span>
                  <span class="block text-xs text-muted-foreground">{formatBytes(att.size)}</span>
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t(msg`Download`)}
                  data-testid="attachment-download"
                  onClick={() => downloadAttachment(att)}
                >
                  <Download class="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t(msg`Delete attachment`)}
                  data-testid="attachment-delete"
                  onClick={() => void vault.deleteAttachment(props.vaultId, att.id)}
                >
                  <Trash2 class="h-4 w-4 text-destructive" />
                </Button>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
}
